import { useState, useEffect, useRef } from 'react'
import { C, font } from '../design'
import { apiFetch } from '../utils/api'

const STAT_LABELS = {
  // Personal
  openness: 'Открытость',
  conscientiousness: 'Добросовестность',
  extraversion: 'Экстраверсия',
  agreeableness: 'Доброжелательность',
  stability: 'Стабильность',
  communication: 'Коммуникация',
  leadership: 'Лидерство',
  confidence: 'Уверенность',
  // Professional
  technical_depth: 'Техн. глубина',
  domain_expertise: 'Экспертиза',
  strategic_thinking: 'Стратегия',
  project_management: 'Управление',
  market_value: 'Рыночная ценность',
}

const TASK_ICONS = {
  read: '📖',
  exercise: '⚡',
  reflection: '🔮',
  quiz: '❓',
  real_world: '🌍',
}

const TASK_LABELS = {
  read: 'Чтение',
  exercise: 'Упражнение',
  reflection: 'Рефлексия',
  quiz: 'Проверка',
  real_world: 'В реальном мире',
}

// ── Skill status colours ─────────────────────────────────────────────────────
const statusStyle = {
  locked:      { ring: 'rgba(180,185,230,0.08)', fill: 'rgba(180,185,230,0.04)', text: C.silverLo, glow: 'none' },
  available:   { ring: 'rgba(180,185,230,0.4)',  fill: 'rgba(180,185,230,0.06)', text: C.silverHi, glow: '0 0 12px rgba(180,185,230,0.2)' },
  in_progress: { ring: '#b8bdd4',                fill: 'rgba(184,189,212,0.1)', text: C.silverHi,  glow: '0 0 18px rgba(184,189,212,0.35)' },
  mastered:    { ring: '#c8a96e',                fill: 'rgba(200,169,110,0.12)', text: '#c8a96e',   glow: '0 0 22px rgba(200,169,110,0.4)' },
}

const statusLabel = { locked: '🔒', available: '○', in_progress: '◎', mastered: '★' }

// ── Stat bonus tags ───────────────────────────────────────────────────────────
function StatBonusTags({ bonuses }) {
  const entries = Object.entries(bonuses || {}).filter(([, v]) => v > 0)
  if (!entries.length) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
      {entries.map(([k, v]) => (
        <span key={k} style={{
          padding: '2px 8px', borderRadius: 12, fontSize: 10,
          background: 'rgba(100,220,150,0.1)', border: '1px solid rgba(100,220,150,0.25)',
          color: 'rgba(100,220,150,0.9)', fontFamily: font.mono,
        }}>
          +{v} {STAT_LABELS[k] || k}
        </span>
      ))}
    </div>
  )
}

// ── Individual task view ──────────────────────────────────────────────────────
function TaskItem({ task, idx, skillTitle, onCheck, onSaveAnswer }) {
  const [answer, setAnswer] = useState(task.user_answer || '')
  const [checking, setChecking] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const isDone = task.status === 'completed'
  const content = task.content || {}

  const handleCheck = async () => {
    if (!answer.trim()) return
    setChecking(true)
    try { await onCheck(task.id, answer) }
    finally { setChecking(false) }
  }

  return (
    <div style={{
      background: isDone
        ? (task.score >= 70 ? 'rgba(100,220,150,0.05)' : 'rgba(230,100,100,0.05)')
        : 'rgba(180,185,230,0.03)',
      border: `1px solid ${isDone ? (task.score >= 70 ? 'rgba(100,220,150,0.2)' : 'rgba(230,100,100,0.2)') : C.border}`,
      borderRadius: 10, overflow: 'hidden', marginBottom: 8,
    }}>
      <div
        onClick={() => !isDone && setExpanded(e => !e)}
        style={{ padding: '12px 16px', cursor: isDone ? 'default' : 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10 }}
      >
        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{TASK_ICONS[task.task_type] || '📋'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: font.body, fontSize: 13, color: C.silverHi }}>
              {idx + 1}. {task.title || TASK_LABELS[task.task_type]}
            </span>
            {task.is_mastery_check && (
              <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 9.5, background: 'rgba(200,169,110,0.15)', color: '#c8a96e', fontFamily: font.mono }}>
                ИТОГОВАЯ ПРОВЕРКА
              </span>
            )}
            {task.task_type === 'real_world' && (
              <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 9.5, background: 'rgba(100,180,255,0.12)', color: 'rgba(100,180,255,0.9)', fontFamily: font.mono }}>
                РЕАЛЬНЫЙ МИР
              </span>
            )}
          </div>
          {isDone && task.claude_feedback && (
            <div style={{ marginTop: 6, fontSize: 12, color: task.score >= 70 ? 'rgba(100,220,150,0.8)' : 'rgba(230,100,100,0.8)', fontFamily: font.body, lineHeight: 1.5 }}>
              {task.claude_feedback}
            </div>
          )}
        </div>
        {isDone ? (
          <span style={{ fontFamily: font.mono, fontSize: 11, color: task.score >= 70 ? 'rgba(100,220,150,0.9)' : 'rgba(230,100,100,0.8)', flexShrink: 0 }}>
            {task.score}%
          </span>
        ) : (
          <span style={{ fontFamily: font.mono, fontSize: 10, color: C.silverLo, flexShrink: 0 }}>{expanded ? '▲' : '▶'}</span>
        )}
      </div>

      {!isDone && expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${C.border}` }}>
          {/* Task content */}
          {task.task_type === 'read' && content.text && (
            <div style={{ marginTop: 12, fontSize: 13, color: C.text, lineHeight: 1.8, fontFamily: font.body, maxHeight: 300, overflowY: 'auto' }}>
              {content.text}
            </div>
          )}
          {task.task_type === 'exercise' && content.instructions && (
            <div style={{ marginTop: 12, fontSize: 13, color: C.text, lineHeight: 1.75, fontFamily: font.body }}>
              {content.instructions}
            </div>
          )}
          {task.task_type === 'real_world' && content.mission && (
            <div style={{ marginTop: 12, padding: '12px 14px', background: 'rgba(100,180,255,0.06)', border: '1px solid rgba(100,180,255,0.18)', borderRadius: 8 }}>
              <div style={{ fontFamily: font.mono, fontSize: 9.5, color: 'rgba(100,180,255,0.7)', letterSpacing: '0.1em', marginBottom: 8 }}>МИССИЯ</div>
              <div style={{ fontSize: 13.5, color: C.silverHi, fontFamily: font.body, lineHeight: 1.7 }}>{content.mission}</div>
              {content.duration && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: C.silverLo, fontFamily: font.mono }}>⏱ {content.duration}</div>
              )}
            </div>
          )}
          {task.task_type === 'reflection' && content.prompts?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {content.prompts.map((q, qi) => (
                <div key={qi} style={{ fontSize: 13, color: C.text, fontFamily: font.body, lineHeight: 1.7, marginBottom: 8 }}>
                  {qi + 1}. {q}
                </div>
              ))}
            </div>
          )}
          {task.task_type === 'quiz' && content.questions?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {content.questions.map((q, qi) => (
                <div key={qi} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: C.silverHi, fontFamily: font.body, marginBottom: 6 }}>{qi + 1}. {q.q}</div>
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} style={{ fontSize: 12, color: C.textMuted, fontFamily: font.body, padding: '2px 0' }}>
                      {['а', 'б', 'в', 'г'][oi] || oi}. {opt}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Answer prompt */}
          {content.check_question && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: C.accent, fontFamily: font.body, fontStyle: 'italic' }}>
              {content.check_question}
            </div>
          )}
          {content.report_prompt && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: C.accent, fontFamily: font.body, fontStyle: 'italic' }}>
              📝 {content.report_prompt}
            </div>
          )}
          {content.evaluation_criteria && (
            <div style={{ marginTop: 10, fontSize: 12, color: C.silverLo, fontFamily: font.body }}>
              Критерии: {content.evaluation_criteria}
            </div>
          )}

          <textarea
            value={answer}
            onChange={e => { setAnswer(e.target.value); onSaveAnswer(task.id, e.target.value) }}
            placeholder={task.task_type === 'real_world' ? 'Напишите отчёт о выполнении...' : task.task_type === 'quiz' ? 'Напишите ваши ответы (например: 1-а, 2-в, 3-б)...' : 'Ваш ответ...'}
            style={{
              width: '100%', marginTop: 12, padding: '10px 12px', minHeight: 100,
              background: 'rgba(180,185,230,0.05)', border: `1px solid ${C.border}`,
              borderRadius: 8, color: C.text, fontFamily: font.body, fontSize: 13,
              resize: 'vertical', outline: 'none', boxSizing: 'border-box',
            }}
          />
          <button
            onClick={handleCheck}
            disabled={!answer.trim() || checking}
            style={{
              marginTop: 10, padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: answer.trim() && !checking ? '#b8bdd4' : 'rgba(180,185,230,0.15)',
              color: answer.trim() && !checking ? '#05050d' : C.silverLo,
              fontFamily: font.mono, fontSize: 12, transition: 'all 0.2s',
            }}
          >
            {checking ? '◌ Проверяю...' : '→ Отправить на проверку'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Skill detail panel ────────────────────────────────────────────────────────
function SkillDetail({ skill, userId, onUpdate }) {
  const [starting, setStarting] = useState(false)
  const tasks = skill.tasks || []
  const completedCount = tasks.filter(t => t.status === 'completed').length
  const totalCount = tasks.length

  const handleStart = async () => {
    setStarting(true)
    try {
      const res = await apiFetch(`/api/dev/skills/${skill.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      })
      if (res.ok) onUpdate()
    } finally {
      setStarting(false)
    }
  }

  const handleCheck = async (taskId, answer) => {
    const res = await apiFetch(`/api/dev/tasks/${taskId}/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answer, user_id: userId }),
    })
    if (res.ok) onUpdate()
  }

  const handleSaveAnswer = async (taskId, answer) => {
    await apiFetch(`/api/dev/tasks/${taskId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_answer: answer }),
    })
  }

  return (
    <div style={{ padding: '16px 20px', background: 'rgba(180,185,230,0.04)', borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 12.5, color: C.textMuted, fontFamily: font.body, lineHeight: 1.7, marginBottom: 12 }}>
        {skill.description}
      </div>
      <StatBonusTags bonuses={skill.stat_bonuses} />

      {skill.status === 'available' && !skill.tasks_generated && (
        <button onClick={handleStart} disabled={starting} style={{
          marginTop: 14, padding: '9px 22px', borderRadius: 8, border: `1px solid ${C.silver}`,
          background: 'transparent', color: C.silverHi, fontFamily: font.mono, fontSize: 12,
          cursor: 'pointer', transition: 'all 0.2s',
        }}>
          {starting ? '◌ Генерирую задания...' : '▶ Начать освоение'}
        </button>
      )}

      {skill.tasks_generated && tasks.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: font.mono, fontSize: 10, color: C.silverLo, letterSpacing: '0.1em' }}>ЗАДАНИЯ</span>
            <span style={{ fontFamily: font.mono, fontSize: 10, color: completedCount === totalCount ? 'rgba(100,220,150,0.8)' : C.silverLo }}>
              {completedCount}/{totalCount}
            </span>
          </div>
          {tasks.map((t, i) => (
            <TaskItem
              key={t.id}
              task={t}
              idx={i}
              skillTitle={skill.title}
              onCheck={handleCheck}
              onSaveAnswer={handleSaveAnswer}
            />
          ))}
        </div>
      )}

      {skill.status === 'mastered' && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: '#c8a96e', fontFamily: font.mono }}>
          ★ Навык освоен
        </div>
      )}
    </div>
  )
}

// ── Skill node ────────────────────────────────────────────────────────────────
function SkillNode({ skill, isLast, userId, onUpdate, isMobile }) {
  const [expanded, setExpanded] = useState(false)
  const st = statusStyle[skill.status] || statusStyle.locked
  const isClickable = skill.status !== 'locked'

  const toggleExpanded = () => {
    if (isClickable) setExpanded(e => !e)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      {/* Connector line above (except first) */}
      {skill.level > 1 && (
        <div style={{
          width: 2, height: 20, alignSelf: 'center',
          background: skill.status === 'mastered'
            ? 'linear-gradient(to bottom, #c8a96e, rgba(200,169,110,0.4))'
            : 'rgba(180,185,230,0.12)',
        }} />
      )}

      {/* Node card */}
      <div
        style={{
          width: '100%',
          border: `1.5px solid ${st.ring}`,
          borderRadius: 12,
          background: st.fill,
          boxShadow: expanded ? st.glow : 'none',
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'box-shadow 0.3s, border-color 0.3s',
          overflow: 'hidden',
        }}
      >
        <div onClick={toggleExpanded} style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: skill.is_ultimate ? 18 : 14, flexShrink: 0, color: st.text }}>
              {skill.status === 'mastered' ? '★' : skill.is_ultimate ? '✦' : statusLabel[skill.status]}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: font.body, fontSize: skill.is_ultimate ? 14 : 13, color: st.text, fontWeight: skill.is_ultimate ? 500 : 400 }}>
                {skill.title}
              </div>
              {skill.is_ultimate && (
                <div style={{ fontFamily: font.mono, fontSize: 9, color: '#c8a96e', letterSpacing: '0.12em', marginTop: 2 }}>УЛЬТИМЕЙТ</div>
              )}
            </div>
            {isClickable && (
              <span style={{ fontFamily: font.mono, fontSize: 9, color: st.text, flexShrink: 0 }}>{expanded ? '▲' : '▶'}</span>
            )}
          </div>
          {/* Stat bonus preview (compact) */}
          {Object.keys(skill.stat_bonuses || {}).length > 0 && !expanded && (
            <div style={{ marginTop: 5, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {Object.entries(skill.stat_bonuses).slice(0, 2).map(([k, v]) => (
                <span key={k} style={{ fontSize: 9.5, color: 'rgba(100,220,150,0.7)', fontFamily: font.mono }}>
                  +{v} {STAT_LABELS[k]}
                </span>
              ))}
            </div>
          )}
        </div>

        {expanded && isClickable && (
          <SkillDetail skill={skill} userId={userId} onUpdate={onUpdate} />
        )}
      </div>

      {/* Connector line below (except last) */}
      {!isLast && (
        <div style={{
          width: 2, height: 20, alignSelf: 'center',
          background: skill.status === 'mastered'
            ? 'linear-gradient(to bottom, rgba(200,169,110,0.4), rgba(180,185,230,0.12))'
            : 'rgba(180,185,230,0.12)',
        }} />
      )}
    </div>
  )
}

// ── Branch column ─────────────────────────────────────────────────────────────
function BranchColumn({ branch, userId, onUpdate, isMobile }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch',
      width: '100%',
    }}>
      {/* Branch header */}
      <div style={{
        width: '100%', marginBottom: 4,
        padding: '10px 14px', borderRadius: 10,
        background: 'rgba(180,185,230,0.04)', border: `1px solid ${C.border}`,
        textAlign: 'center',
      }}>
        <div style={{ fontFamily: font.mono, fontSize: 9, color: C.silverLo, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
          ВЕТКА
        </div>
        <div style={{ fontFamily: font.body, fontSize: 13, color: C.accent }}>
          {branch.title?.replace('Ветка: ', '') || branch.title}
        </div>
        {branch.ultimate_title && (
          <div style={{ fontFamily: font.mono, fontSize: 9.5, color: 'rgba(200,169,110,0.6)', marginTop: 3 }}>
            → {branch.ultimate_title}
          </div>
        )}
        {branch.evidence && (() => {
          let items = []
          try { const p = JSON.parse(branch.evidence); items = Array.isArray(p) ? p : [branch.evidence] }
          catch { items = [branch.evidence] }
          return (
            <div style={{
              marginTop: 8, padding: '7px 10px', borderRadius: 7,
              background: 'rgba(184,189,212,0.05)',
              borderLeft: '2px solid rgba(184,189,212,0.25)',
            }}>
              <div style={{ fontFamily: font.mono, fontSize: 8, color: C.silverLo, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                ПОЧЕМУ ЭТА ВЕТКА
              </div>
              {items.map((item, i) => (
                <div key={i} style={{ fontFamily: font.body, fontSize: 11, color: 'rgba(184,189,212,0.7)', lineHeight: 1.5, textAlign: 'left', marginBottom: i < items.length - 1 ? 4 : 0 }}>
                  · {item}
                </div>
              ))}
            </div>
          )
        })()}
      </div>

      {/* Skills chain */}
      {(branch.skills || []).map((skill, idx) => (
        <SkillNode
          key={skill.id}
          skill={skill}
          isLast={idx === branch.skills.length - 1}
          userId={userId}
          onUpdate={onUpdate}
          isMobile={isMobile}
        />
      ))}
    </div>
  )
}

// ── Track card (for selection screen) ────────────────────────────────────────
function TrackCard({ track, isSelected, onSelect, isMobile }) {
  return (
    <div style={{
      width: '100%',
      padding: '22px 24px',
      borderRadius: 14,
      background: isSelected ? 'rgba(200,169,110,0.08)' : 'rgba(180,185,230,0.04)',
      border: `1.5px solid ${isSelected ? 'rgba(200,169,110,0.5)' : C.border}`,
      cursor: 'pointer', transition: 'all 0.25s',
      boxShadow: isSelected ? '0 0 24px rgba(200,169,110,0.15)' : 'none',
    }} onClick={() => onSelect(track.id, !isSelected)}>
      <div style={{
        fontFamily: font.mono, fontSize: 9, letterSpacing: '0.15em',
        color: isSelected ? '#c8a96e' : C.silverLo,
        textTransform: 'uppercase', marginBottom: 8,
      }}>
        {track.archetype_title || 'ТРЕК'} {isSelected ? '✓' : ''}
      </div>
      <div style={{ fontFamily: font.heading, fontSize: isMobile ? 22 : 26, fontStyle: 'italic', color: C.silverHi, marginBottom: 8, lineHeight: 1.2 }}>
        {track.title}
      </div>
      <div style={{ fontFamily: font.body, fontSize: 12.5, color: C.accent, fontStyle: 'italic', marginBottom: 12, lineHeight: 1.5 }}>
        {track.tagline}
      </div>
      <div style={{ fontFamily: font.body, fontSize: 12.5, color: C.textMuted, lineHeight: 1.75, marginBottom: 14 }}>
        {track.description}
      </div>
      {track.potential_outcome && (
        <div style={{
          padding: '10px 12px', borderRadius: 8,
          background: isSelected ? 'rgba(200,169,110,0.1)' : 'rgba(180,185,230,0.05)',
          border: `1px solid ${isSelected ? 'rgba(200,169,110,0.2)' : C.border}`,
          fontSize: 12, color: isSelected ? '#c8a96e' : C.silverLo, fontFamily: font.body, fontStyle: 'italic', lineHeight: 1.6,
        }}>
          ✦ {track.potential_outcome}
        </div>
      )}
      <div style={{ marginTop: 14, fontFamily: font.mono, fontSize: 9, color: C.silverLo }}>
        {track.branch_count || 0} веток развития
      </div>
    </div>
  )
}

// ── Main SkillTree component ──────────────────────────────────────────────────
export default function SkillTree({ userId, isOwner, isMobile, category = 'personal' }) {
  const [tracks, setTracks]       = useState([])
  const [tree, setTree]           = useState([])
  const [loading, setLoading]     = useState(true)
  const [generating, setGenerating] = useState(false)
  const [genMsg, setGenMsg]       = useState('')
  const [genError, setGenError]   = useState('')
  const [activeTrack, setActiveTrack] = useState(null)
  const [tip, setTip]             = useState('')
  const pollRef = useRef(null)
  const BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

  const loadAll = async () => {
    try {
      const [tr, tree2, tipRes] = await Promise.all([
        fetch(`${BASE}/api/dev/tracks/${userId}?category=${category}`).then(r => r.json()),
        fetch(`${BASE}/api/dev/tree/${userId}?category=${category}`).then(r => r.json()),
        fetch(`${BASE}/api/dev/tip/${userId}`).then(r => r.json()),
      ])
      setTracks(tr.tracks || [])
      setTree(tree2.tracks || [])
      setTip(tipRes.tip || '')
      if (!activeTrack && (tree2.tracks || []).some(t => t.is_selected)) {
        setActiveTrack((tree2.tracks || []).find(t => t.is_selected)?.id || null)
      }
    } catch {}
    setLoading(false)
  }

  useEffect(() => { loadAll() }, [userId, category])

  const generateTracks = async () => {
    setGenerating(true)
    setGenError('')
    setGenMsg('Claude анализирует ваш профиль и строит карту развития...')
    try {
      const res = await apiFetch(`/api/dev/generate-tracks/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      })
      const { job_id } = await res.json()
      if (!job_id) { setGenerating(false); return }

      const poll = async () => {
        try {
          const r = await fetch(`${BASE}/api/analyze/job/${job_id}`)
          const d = await r.json()
          if (d.status === 'done') {
            clearInterval(pollRef.current)
            pollRef.current = null
            await loadAll()
            setGenerating(false)
          } else if (d.status === 'error') {
            clearInterval(pollRef.current)
            pollRef.current = null
            setGenerating(false)
            setGenError(d.error || 'Ошибка генерации. Попробуй снова.')
          } else {
            setGenMsg('Claude строит ваш путь развития...')
          }
        } catch {}
      }
      pollRef.current = setInterval(poll, 3000)
      setTimeout(() => {
        if (pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
          setGenerating(false)
          setGenError('Генерация заняла слишком долго. Попробуй снова.')
        }
      }, 180000)
    } catch {
      setGenerating(false)
      setGenError('Ошибка соединения. Попробуй снова.')
    }
  }

  useEffect(() => () => clearInterval(pollRef.current), [])

  const handleSelect = async (trackId, selected) => {
    await apiFetch(`/api/dev/tracks/${trackId}/select`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected }),
    })
    await loadAll()
    if (selected) setActiveTrack(trackId)
  }

  const clearTree = async () => {
    if (!confirm('Сбросить дерево навыков для этой категории? Прогресс будет потерян.')) return
    await apiFetch(`/api/dev/tree/${userId}?category=${category}`, { method: 'DELETE' })
    setTracks([]); setTree([]); setActiveTrack(null)
  }

  const hasSelectedTracks = tree.some(t => t.is_selected)
  const activeBranches = hasSelectedTracks
    ? (tree.find(t => t.id === activeTrack)?.branches || tree.filter(t => t.is_selected).flatMap(t => t.branches))
    : []

  if (loading) {
    return <div style={{ color: C.silverLo, fontFamily: font.body, fontSize: 13, padding: '40px 0', textAlign: 'center' }}>◌ Загрузка...</div>
  }

  return (
    <div style={{ padding: '0 0 80px' }}>
      {/* Daily tip */}
      {tip && (
        <div style={{
          padding: '12px 18px', marginBottom: 24, borderRadius: 10,
          background: 'rgba(180,185,230,0.04)', border: `1px solid ${C.border}`,
          fontFamily: font.body, fontSize: 12.5, color: C.textMuted, fontStyle: 'italic', lineHeight: 1.7,
        }}>
          ✦ {tip}
        </div>
      )}

      {/* Empty state — no tracks */}
      {tracks.length === 0 && !generating && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          {genError && (
            <div style={{ marginBottom: 20, fontFamily: font.body, fontSize: 13, color: 'rgba(230,100,100,0.8)' }}>
              {genError}
            </div>
          )}
          <div style={{ fontFamily: font.heading, fontSize: isMobile ? 26 : 34, fontStyle: 'italic', color: C.silverHi, marginBottom: 12 }}>
            Выбери свой путь
          </div>
          <div style={{ fontFamily: font.body, fontSize: 13.5, color: C.textMuted, marginBottom: 32, maxWidth: 400, margin: '0 auto 32px' }}>
            Claude проанализирует твой профиль и предложит 2–3 трека развития — как выбор класса в RPG.
            Каждый трек ведёт к уникальному мастерству через последовательные навыки.
          </div>
          <button onClick={generateTracks} style={{
            padding: '13px 32px', borderRadius: 10, border: `1px solid ${C.silver}`,
            background: 'transparent', color: C.silverHi, fontFamily: font.mono, fontSize: 13,
            cursor: 'pointer', letterSpacing: '0.05em', transition: 'all 0.2s',
          }}>
            ◈ Составить план развития
          </button>
        </div>
      )}

      {/* Generating animation */}
      {generating && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', margin: '0 auto 20px',
            border: '2px solid rgba(180,185,230,0.15)',
            borderTop: '2px solid #b8bdd4',
            animation: 'spin 1.5s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          <div style={{ fontFamily: font.body, fontSize: 14, color: C.silverLo }}>{genMsg}</div>
        </div>
      )}

      {/* Track selection */}
      {tracks.length > 0 && (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: '0.15em', color: C.silverLo, textTransform: 'uppercase' }}>
                ТРЕКИ РАЗВИТИЯ — выбери один или несколько
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {isOwner && (
                  <button onClick={generateTracks} disabled={generating} style={{
                    padding: '5px 12px', borderRadius: 7, border: `1px solid ${C.border}`,
                    background: 'transparent', color: C.silverLo, fontFamily: font.mono, fontSize: 10,
                    cursor: 'pointer',
                  }}>↻ пересобрать</button>
                )}
                {isOwner && (
                  <button onClick={clearTree} style={{
                    padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(230,100,100,0.2)',
                    background: 'transparent', color: 'rgba(230,100,100,0.6)', fontFamily: font.mono, fontSize: 10,
                    cursor: 'pointer',
                  }}>✕ сбросить</button>
                )}
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: isMobile ? 12 : 16,
            }}>
              {tracks.map(t => (
                <TrackCard
                  key={t.id}
                  track={t}
                  isSelected={tree.find(tr => tr.id === t.id)?.is_selected || false}
                  onSelect={handleSelect}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </div>

          {/* Active track tabs (if multiple selected) */}
          {hasSelectedTracks && tree.filter(t => t.is_selected).length > 1 && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}` }}>
              {tree.filter(t => t.is_selected).map(t => (
                <button key={t.id} onClick={() => setActiveTrack(t.id)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '8px 16px',
                  fontFamily: font.body, fontSize: 13,
                  color: activeTrack === t.id ? C.silverHi : C.silverLo,
                  borderBottom: `2px solid ${activeTrack === t.id ? C.silver : 'transparent'}`,
                  marginBottom: -1, transition: 'color 0.15s',
                }}>{t.title}</button>
              ))}
            </div>
          )}

          {/* Skill tree branches */}
          {hasSelectedTracks && activeBranches.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? '1fr'
                : `repeat(${Math.min(activeBranches.length, 4)}, 1fr)`,
              gap: isMobile ? 24 : 20,
              alignItems: 'start',
              width: '100%',
            }}>
              {activeBranches.map(branch => (
                <BranchColumn
                  key={branch.id}
                  branch={branch}
                  userId={userId}
                  onUpdate={loadAll}
                  isMobile={isMobile}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
